import { enableProdMode, ViewEncapsulation, provideZonelessChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { getLogConfigWithDefaults, LOG_CONFIG } from '@config/log-config.types';
import { environment } from '@environments/environment';
import { APP_SETTINGS } from '@config/app-settings';
import { APP_CONFIG, getAppConfigWithDefaults } from '@config/app-config.types';
import { BFF_CONFIG, getBFFConfigWithDefaults } from '@services/bff-config.types';
import {
  getVisualisatieServiceConfigWithDefaults,
  VISUALISATIESERVICE_CONFIG,
} from '@services/visualisatieservice-config.types';
import { AUTH_CONFIG, getAuthConfigWithDefaults } from '@config/auth-config.types';

const configuration = { ...environment };

if (environment.production) {
  enableProdMode();
}

// When logging in to localhost or via medewerker-*.doe/ocw-doorstroomtoets.nl, use AuthIntern settings
// This applies for internal (medewerker) authentication
if (
  Object.hasOwnProperty.call(environment, 'AuthIntern') &&
  RegExp('((http|https)://medewerker)|((http|https)://localhost)').test(window.location.href)
) {
   
  // @ts-ignore
  configuration.Auth = environment.AuthIntern;
}

/**
 * Bootstrap the Angular application
 * If MSW is enabled, initialize it first before bootstrapping
 */
async function bootstrap() {
  await initializeMSW();
  bootstrapAngularApp();
}

async function initializeMSW(): Promise<void> {
  if (environment.useMSW) {
    (window as unknown as Record<string, unknown>)['__APP_VARIANT__'] = environment.variant;
    await startMSWService();
    return;
  }

  await unregisterMSWServiceWorkerIfExists();
}

async function startMSWService(): Promise<void> {
  const { startMSW } = await import('../msw/setup');
  await startMSW();
}

async function unregisterMSWServiceWorkerIfExists(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await unregisterMSWRegistrations(registrations);
}

async function unregisterMSWRegistrations(registrations: readonly ServiceWorkerRegistration[]): Promise<void> {
  for (const registration of registrations) {
    if (!isMSWServiceWorker(registration)) {
      continue;
    }

    await registration.unregister();
    console.info('🔧 Unregistered MSW service worker (not needed in this mode)');
  }
}

function isMSWServiceWorker(registration: ServiceWorkerRegistration): boolean {
  return registration.active?.scriptURL.includes('mockServiceWorker.js') ?? false;
}

function bootstrapAngularApp(): void {
  const providers = createAppProviders();

  platformBrowserDynamic(providers)
    .bootstrapModule(AppModule, {
      applicationProviders: [provideZonelessChangeDetection()],
      defaultEncapsulation: ViewEncapsulation.Emulated,
    })
     
    .catch((err) => console.error(err));
}

function createAppProviders() {
  return [
    { provide: APP_SETTINGS, useValue: configuration },
    { provide: LOG_CONFIG, useValue: getLogConfigWithDefaults(configuration) },
    { provide: AUTH_CONFIG, useValue: getAuthConfigWithDefaults(configuration) },
    { provide: APP_CONFIG, useValue: getAppConfigWithDefaults(configuration) },
    { provide: BFF_CONFIG, useValue: getBFFConfigWithDefaults(configuration) },
    { provide: VISUALISATIESERVICE_CONFIG, useValue: getVisualisatieServiceConfigWithDefaults(configuration) },
  ];
}

bootstrap();
